import {Suspense, lazy, StrictMode, useCallback, useMemo} from 'react'
import {createRoot} from 'react-dom/client'
import {BrowserRouter, Routes, Route, Navigate} from 'react-router-dom'
import {useTranslation} from 'react-i18next'
import {
  PlaygroundApp,
  HomePage,
  ConversationsListPage,
  ChatRoute,
  ShareArtifactPage,
  NotFoundPage,
  SkillsPage,
  SkillsNewPage,
  SkillDetailPage,
  ToastProvider,
  AuthProvider,
  useAuth,
  useCoreSettingsSections,
  useCoreAccountSections,
  settingsRoutes,
  accountRoutes,
  settingsSectionPath,
  accountSectionPath,
  configureApi,
  type AuthUser
} from '@livingdata/playground-core-front'
import '@livingdata/playground-core-front/styles.css'
import '@fontsource-variable/montserrat/index.css'
import '@fontsource-variable/montserrat/wght-italic.css'
import '@fontsource-variable/jetbrains-mono/index.css'
import '@fontsource-variable/jetbrains-mono/wght-italic.css'
import './brand.css'
import '@livingdata/playground-core-front/i18n'
import './i18n'
import {brand} from './brand'
import {LivingDataLogo} from './brand/logo'
import {authClient} from './auth-client'
import {SignInPage} from './pages/sign-in'
import {SignUpPage} from './pages/sign-up'
import {ForgotPasswordPage} from './pages/forgot-password'
import {ResetPasswordPage} from './pages/reset-password'
import {AccountPage} from './pages/account'
import {LandingPage} from './pages/landing'
import {ContactPage} from './pages/contact'
import {ComingSoonPage} from './pages/coming-soon'
import {LegalDocument} from './pages/legal-document'
import {PublicLayout} from './layouts/public-layout'
import mentionsLegales from './legal/mentions-legales.md?raw'
import cguv from './legal/cguv.md?raw'
import politiqueConfidentialite from './legal/politique-de-confidentialite.md?raw'

const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:3000'
// credentials:'include' so the Better Auth session cookie travels cross-origin.
configureApi({apiUrl, credentials: 'include'})

// Loaded on demand, and never imported statically anywhere: the admin console
// has no business in the bundle every end user downloads. Its stylesheet
// travels separately because the built package's JS does not reference it.
const AdminConsole = lazy(async () => {
  const [module] = await Promise.all([
    import('@livingdata/playground-admin-front'),
    import('@livingdata/playground-admin-front/styles.css')
  ])
  return {default: module.AdminRoutes}
})

function BrandSplash() {
  return (
    <div className='flex h-screen w-full items-center justify-center bg-background'>
      <span className='block h-12 w-12 animate-pulse'><LivingDataLogo variant='compact' /></span>
    </div>
  )
}

function UserIcon() {
  return (
    <svg
      width='16'
      height='16'
      viewBox='0 0 24 24'
      fill='none'
      stroke='currentColor'
      strokeWidth='1.75'
      strokeLinecap='round'
      strokeLinejoin='round'
      aria-hidden='true'
    >
      <path d='M19 21v-2a4 4 0 0 0-4-4H9a4 4 0 0 0-4 4v2' />
      <circle cx='12' cy='7' r='4' />
    </svg>
  )
}

const PROFILE_SECTION = 'profile'

function AuthenticatedRoutes({logout}: {readonly logout: () => void | Promise<void>}) {
  const {t} = useTranslation()
  const settingsSections = useCoreSettingsSections()
  const coreAccountSections = useCoreAccountSections()
  const accountSections = useMemo(() => [
    {id: PROFILE_SECTION, path: PROFILE_SECTION, label: t('auth.account.title'), icon: <UserIcon />, element: <AccountPage />},
    ...coreAccountSections
  ], [coreAccountSections, t])
  return (
    <Routes>
      <Route element={<PlaygroundApp brand={brand} loginPage={<Navigate to='/' replace />} logout={logout} settingsSections={settingsSections} accountSections={accountSections} />}>
        <Route path='/' element={<HomePage />} />
        <Route path='/conversations/new' element={<ChatRoute />} />
        <Route path='/conversations' element={<ConversationsListPage />} />
        <Route path='/conversations/:conversationId' element={<ChatRoute />} />
        <Route path='/skills' element={<SkillsPage />} />
        <Route path='/skills/new' element={<SkillsNewPage />} />
        <Route path='/skills/:skillId' element={<SkillDetailPage />} />
        {settingsRoutes(settingsSections)}
        {accountRoutes(accountSections)}
        {/* Former URL of the profile page, kept for existing links. */}
        <Route path={settingsSectionPath('account')} element={<Navigate to={accountSectionPath(PROFILE_SECTION)} replace />} />
      </Route>

      <Route
        path='/admin/*'
        element={
          <Suspense fallback={null}>
            <AdminConsole apiUrl={apiUrl} basePath='/admin' />
          </Suspense>
        }
      />
      <Route path='/share/:shareToken' element={<ShareArtifactPage />} />
      {/* Legacy share URLs — token is read from the `shareToken` query param */}
      <Route path='/share/:conversationId/:artifactSlug' element={<ShareArtifactPage />} />

      <Route path='*' element={<NotFoundPage />} />
    </Routes>
  )
}

function PublicRoutes() {
  return (
    <Routes>
      <Route element={<PublicLayout />}>
        <Route path='/' element={<LandingPage />} />
        <Route path='/sign-in' element={<SignInPage />} />
        <Route path='/sign-up' element={<SignUpPage />} />
        <Route path='/forgot-password' element={<ForgotPasswordPage />} />
        <Route path='/reset-password' element={<ResetPasswordPage />} />
        <Route path='/discover' element={<ComingSoonPage />} />
        <Route path='/community' element={<ComingSoonPage />} />
        <Route path='/pricing' element={<ComingSoonPage />} />
        <Route path='/docs' element={<ComingSoonPage />} />
        <Route path='/contact' element={<ContactPage />} />
        <Route path='/legal' element={<LegalDocument source={mentionsLegales} />} />
        <Route path='/terms' element={<LegalDocument source={cguv} />} />
        <Route path='/privacy' element={<LegalDocument source={politiqueConfidentialite} />} />
      </Route>

      <Route path='/share/:shareToken' element={<ShareArtifactPage />} />
      {/* Legacy share URLs — token is read from the `shareToken` query param */}
      <Route path='/share/:conversationId/:artifactSlug' element={<ShareArtifactPage />} />

      {/* Visiteur non connecté sur une URL protégée/inconnue : renvoyer vers la connexion. */}
      <Route path='*' element={<Navigate to='/sign-in' replace />} />
    </Routes>
  )
}

function AppRoutes({logout}: {readonly logout: () => void | Promise<void>}) {
  const {isLoading, isAuthenticated} = useAuth()

  if (isLoading) {
    return <BrandSplash />
  }

  return isAuthenticated ? <AuthenticatedRoutes logout={logout} /> : <PublicRoutes />
}

function App() {
  // Resolve the current user from the Better Auth session rather than the core
  // GET /user (which 401s when anonymous and would spam the console).
  const loadUser = useCallback(async (): Promise<AuthUser | undefined> => {
    const {data} = await authClient.getSession()
    if (!data?.user) {
      return undefined
    }

    return {id: data.user.id, email: data.user.email, name: data.user.name}
  }, [])

  const logout = useCallback(async () => {
    await authClient.signOut()
    globalThis.location.href = '/'
  }, [])

  return (
    <StrictMode>
      <AuthProvider fetchUser={loadUser}>
        <ToastProvider>
          <BrowserRouter>
            <AppRoutes logout={logout} />
          </BrowserRouter>
        </ToastProvider>
      </AuthProvider>
    </StrictMode>
  )
}

createRoot(document.getElementById('root')!).render(<App />)
